// src/pages/trading-portal/VolatilityArbitrage/PnlAttributionPage.js
// P&L Attribution - 3-tab interface: P&L Decomposition, Strategy Performance, Benchmarks

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    PnlComponent, PnlComponentLabels, PnlComponentColors,
    VAEBenchmarkType, VAEBenchmarkTypeLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const cellSx = { fontFamily, color: '#1e293b', fontSize: '0.82rem' };

const PnlAttributionPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: P&L DECOMPOSITION ====================
    const [pnlData, setPnlData] = useState([]);
    const [pnlLoading, setPnlLoading] = useState(true);
    const [pnlPage, setPnlPage] = useState(0);
    const [pnlPerPage, setPnlPerPage] = useState(10);
    const [pnlTotal, setPnlTotal] = useState(0);
    const [pnlStrategyFilter, setPnlStrategyFilter] = useState('');
    const [pnlDateFrom, setPnlDateFrom] = useState('');
    const [pnlDateTo, setPnlDateTo] = useState('');
    const [calculatingPnlId, setCalculatingPnlId] = useState(null);

    // ==================== TAB 1: STRATEGY PERFORMANCE ====================
    const [perfData, setPerfData] = useState([]);
    const [perfLoading, setPerfLoading] = useState(false);
    const [perfPage, setPerfPage] = useState(0);
    const [perfPerPage, setPerfPerPage] = useState(10);
    const [perfTotal, setPerfTotal] = useState(0);

    // ==================== TAB 2: BENCHMARKS ====================
    const [benchData, setBenchData] = useState([]);
    const [benchLoading, setBenchLoading] = useState(false);
    const [benchPage, setBenchPage] = useState(0);
    const [benchPerPage, setBenchPerPage] = useState(10);
    const [benchTotal, setBenchTotal] = useState(0);

    // ==================== FORMAT HELPERS ====================
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatPct = (val) => val != null ? `${parseFloat(val).toFixed(2)}%` : '-';
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

    const getPnlColor = (val) => {
        if (val == null) return '#64748b';
        const num = parseFloat(val);
        if (num > 0) return '#22c55e';
        if (num < 0) return '#ef4444';
        return '#64748b';
    };

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, edit] = await Promise.all([
                hasPermission('tr-vae-pnl-attribution', 'view'),
                hasPermission('tr-vae-pnl-attribution', 'edit'),
            ]);
            setCanView(view);
            setCanEdit(edit);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: P&L DECOMPOSITION ====================
    const fetchPnlData = useCallback(async () => {
        if (!canView) return;
        setPnlLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: pnlPage + 1, per_page: pnlPerPage });
            if (pnlStrategyFilter) params.append('strategy_id', pnlStrategyFilter);
            if (pnlDateFrom) params.append('date_from', pnlDateFrom);
            if (pnlDateTo) params.append('date_to', pnlDateTo);
            const res = await axiosInstance.get(`/api/volatility-arbitrage/pnl-attributions?${params.toString()}`);
            if (res.data.success) {
                setPnlData(res.data.data || []);
                setPnlTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch P&L decomposition data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch P&L decomposition data');
        } finally {
            setPnlLoading(false);
        }
    }, [canView, pnlPage, pnlPerPage, pnlStrategyFilter, pnlDateFrom, pnlDateTo]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchPnlData();
    }, [fetchPnlData, canView, activeTab]);

    const handleCalculatePnl = async (strategyId) => {
        setCalculatingPnlId(strategyId);
        try {
            const res = await axiosInstance.post(`/api/volatility-arbitrage/strategies/${strategyId}/calculate-pnl`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'P&L calculation completed successfully', severity: 'success' });
                fetchPnlData();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'P&L calculation failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'P&L calculation failed', severity: 'error' });
        } finally {
            setCalculatingPnlId(null);
        }
    };

    // ==================== TAB 1: STRATEGY PERFORMANCE ====================
    const fetchPerfData = useCallback(async () => {
        if (!canView) return;
        setPerfLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: perfPage + 1, per_page: perfPerPage });
            const res = await axiosInstance.get(`/api/volatility-arbitrage/strategy-performance?${params.toString()}`);
            if (res.data.success) {
                setPerfData(res.data.data || []);
                setPerfTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch strategy performance data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch strategy performance data');
        } finally {
            setPerfLoading(false);
        }
    }, [canView, perfPage, perfPerPage]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchPerfData();
    }, [fetchPerfData, canView, activeTab]);

    // ==================== TAB 2: BENCHMARKS ====================
    const fetchBenchData = useCallback(async () => {
        if (!canView) return;
        setBenchLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: benchPage + 1, per_page: benchPerPage });
            const res = await axiosInstance.get(`/api/volatility-arbitrage/benchmarks?${params.toString()}`);
            if (res.data.success) {
                setBenchData(res.data.data || []);
                setBenchTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch benchmark data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch benchmark data');
        } finally {
            setBenchLoading(false);
        }
    }, [canView, benchPage, benchPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchBenchData();
    }, [fetchBenchData, canView, activeTab]);

    // ==================== CSV EXPORT HELPER ====================
    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) return;
        const headers = Object.keys(data[0]);
        const csvRows = [
            headers.join(','),
            ...data.map(row =>
                headers.map(h => {
                    const val = row[h] == null ? '' : String(row[h]);
                    return val.includes(',') || val.includes('"') || val.includes('\n')
                        ? `"${val.replace(/"/g, '""')}"`
                        : val;
                }).join(',')
            ),
        ];
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ==================== RENDER: LOADING SKELETON ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    // ==================== RENDER: TAB 0 - P&L DECOMPOSITION ====================
    const renderPnlDecomposition = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    P&L Decomposition
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(pnlData, 'pnl_decomposition.csv')}
                        disabled={!pnlData.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchPnlData}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Strategy ID"
                    type="number"
                    value={pnlStrategyFilter}
                    onChange={(e) => { setPnlStrategyFilter(e.target.value); setPnlPage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' }, maxWidth: 140 }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
                <TextField
                    size="small"
                    label="Date From"
                    type="date"
                    value={pnlDateFrom}
                    onChange={(e) => { setPnlDateFrom(e.target.value); setPnlPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' }, minWidth: 150 }}
                />
                <TextField
                    size="small"
                    label="Date To"
                    type="date"
                    value={pnlDateTo}
                    onChange={(e) => { setPnlDateTo(e.target.value); setPnlPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' }, minWidth: 150 }}
                />
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {pnlLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                        <TableCell sx={headCellSx}>Date</TableCell>
                                        <TableCell sx={headCellSx}>Delta P&L</TableCell>
                                        <TableCell sx={headCellSx}>Gamma P&L</TableCell>
                                        <TableCell sx={headCellSx}>Theta P&L</TableCell>
                                        <TableCell sx={headCellSx}>Vega P&L</TableCell>
                                        <TableCell sx={headCellSx}>Residual</TableCell>
                                        <TableCell sx={headCellSx}>Total P&L</TableCell>
                                        <TableCell sx={headCellSx}>Cumulative P&L</TableCell>
                                        {canEdit && <TableCell sx={headCellSx}>Actions</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pnlData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={canEdit ? 10 : 9} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.BarChart sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No P&L attribution data found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : pnlData.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.strategy_id || '-'}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatDate(row.date)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.delta_pnl) }}>
                                                {formatPrice(row.delta_pnl)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.gamma_pnl) }}>
                                                {formatPrice(row.gamma_pnl)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.theta_pnl) }}>
                                                {formatPrice(row.theta_pnl)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.vega_pnl) }}>
                                                {formatPrice(row.vega_pnl)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.residual) }}>
                                                {formatPrice(row.residual)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: getPnlColor(row.total_pnl) }}>
                                                {formatPrice(row.total_pnl)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: getPnlColor(row.cumulative_pnl) }}>
                                                {formatPrice(row.cumulative_pnl)}
                                            </TableCell>
                                            {canEdit && (
                                                <TableCell>
                                                    <Tooltip title="Calculate P&L">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<MuiIcons.Calculate />}
                                                                onClick={() => handleCalculatePnl(row.strategy_id)}
                                                                disabled={calculatingPnlId === row.strategy_id}
                                                                sx={{
                                                                    fontFamily,
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.75rem',
                                                                    borderColor: ACCENT,
                                                                    color: ACCENT,
                                                                    '&:hover': { borderColor: ACCENT, bgcolor: '#fffbeb' },
                                                                }}
                                                            >
                                                                {calculatingPnlId === row.strategy_id ? 'Calculating...' : 'Calculate P&L'}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={pnlTotal}
                            page={pnlPage}
                            onPageChange={(_, p) => setPnlPage(p)}
                            rowsPerPage={pnlPerPage}
                            onRowsPerPageChange={(e) => { setPnlPerPage(parseInt(e.target.value, 10)); setPnlPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - STRATEGY PERFORMANCE ====================
    const renderStrategyPerformance = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Strategy Performance
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(perfData, 'strategy_performance.csv')}
                        disabled={!perfData.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchPerfData}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {perfLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                        <TableCell sx={headCellSx}>Period Start</TableCell>
                                        <TableCell sx={headCellSx}>Period End</TableCell>
                                        <TableCell sx={headCellSx}>Total Return (%)</TableCell>
                                        <TableCell sx={headCellSx}>Sharpe Ratio</TableCell>
                                        <TableCell sx={headCellSx}>Sortino Ratio</TableCell>
                                        <TableCell sx={headCellSx}>Win Rate (%)</TableCell>
                                        <TableCell sx={headCellSx}>Max Drawdown (%)</TableCell>
                                        <TableCell sx={headCellSx}>Avg Hold Days</TableCell>
                                        <TableCell sx={headCellSx}>Total Trades</TableCell>
                                        <TableCell sx={headCellSx}>Profit Factor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {perfData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.TrendingUp sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No strategy performance data found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : perfData.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.strategy_id || '-'}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatDate(row.period_start)}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatDate(row.period_end)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.total_return_pct) }}>
                                                {formatPct(row.total_return_pct)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.sharpe_ratio != null ? parseFloat(row.sharpe_ratio).toFixed(3) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.sortino_ratio != null ? parseFloat(row.sortino_ratio).toFixed(3) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.win_rate_pct) }}>
                                                {formatPct(row.win_rate_pct)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#ef4444' }}>
                                                {formatPct(row.max_drawdown_pct)}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {row.avg_hold_days != null ? parseFloat(row.avg_hold_days).toFixed(1) : '-'}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatNumber(row.total_trades)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.profit_factor != null ? parseFloat(row.profit_factor).toFixed(2) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={perfTotal}
                            page={perfPage}
                            onPageChange={(_, p) => setPerfPage(p)}
                            rowsPerPage={perfPerPage}
                            onRowsPerPageChange={(e) => { setPerfPerPage(parseInt(e.target.value, 10)); setPerfPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 2 - BENCHMARKS ====================
    const renderBenchmarks = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Benchmarks
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(benchData, 'benchmarks.csv')}
                        disabled={!benchData.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchBenchData}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {benchLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Strategy ID</TableCell>
                                        <TableCell sx={headCellSx}>Benchmark Type</TableCell>
                                        <TableCell sx={headCellSx}>Period Start</TableCell>
                                        <TableCell sx={headCellSx}>Period End</TableCell>
                                        <TableCell sx={headCellSx}>Strategy Return (%)</TableCell>
                                        <TableCell sx={headCellSx}>Benchmark Return (%)</TableCell>
                                        <TableCell sx={headCellSx}>Alpha (%)</TableCell>
                                        <TableCell sx={headCellSx}>Tracking Error</TableCell>
                                        <TableCell sx={headCellSx}>Info Ratio</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {benchData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.Leaderboard sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No benchmark data found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : benchData.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.strategy_id || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={VAEBenchmarkTypeLabels[row.benchmark_type] || row.benchmark_type}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        bgcolor: '#f1f5f9',
                                                        color: '#475569',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatDate(row.period_start)}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {formatDate(row.period_end)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.strategy_return_pct) }}>
                                                {formatPct(row.strategy_return_pct)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: getPnlColor(row.benchmark_return_pct) }}>
                                                {formatPct(row.benchmark_return_pct)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 700, color: getPnlColor(row.alpha_pct) }}>
                                                {formatPct(row.alpha_pct)}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.tracking_error != null ? parseFloat(row.tracking_error).toFixed(4) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                                {row.info_ratio != null ? parseFloat(row.info_ratio).toFixed(3) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={benchTotal}
                            page={benchPage}
                            onPageChange={(_, p) => setBenchPage(p)}
                            rowsPerPage={benchPerPage}
                            onRowsPerPageChange={(e) => { setBenchPerPage(parseInt(e.target.value, 10)); setBenchPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view P&L Attribution data.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>
                    P&L Attribution
                </Typography>
                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                    Analyze profit and loss decomposition, strategy performance metrics, and benchmark comparisons
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                    }}
                >
                    <Tab label="P&L Decomposition" icon={<MuiIcons.BarChart sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label="Strategy Performance" icon={<MuiIcons.TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab label="Benchmarks" icon={<MuiIcons.Leaderboard sx={{ fontSize: 18 }} />} iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 2.5 }}>
                    {activeTab === 0 && renderPnlDecomposition()}
                    {activeTab === 1 && renderStrategyPerformance()}
                    {activeTab === 2 && renderBenchmarks()}
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PnlAttributionPage;
